// react
import { useCallback, useEffect, useMemo, useState } from 'react';

// mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { default as Grid } from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import { default as MUILink } from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Refresh from '@mui/icons-material/Refresh';

// dayjs
import dayjs from 'dayjs';

// project utils
import handleError from 'utils-ts/handle-error';
import callAzureFunction from 'utils-ts/call-azure-function';

//= =============================|| EmailEventLogsField ||==============================//

/**
 * A single SendGrid email activity event, as returned by the `sendgrid/get-latest-email-event`
 * azure function (backed by the SendGrid Email Activity API `/v3/messages` endpoint).
 * Only the fields consumed by this component are declared; the response may contain
 * additional SendGrid-specific fields that are not used here.
 */
interface EmailEvent {
  msg_id?: string;
  last_event_time?: string;
  status?: string;
  to_email?: string;
  subject?: string;
  [key: string]: unknown;
}

interface EmailEventLogsFieldProps {
  /** Whether the parent booking dialog is open (used to reset state on close/change) */
  open: boolean;
  /** ID of the booking to fetch email logs for */
  bookingId: string | number;
  /** App data source id, if applicable */
  appDataSourceId?: string | number;
  /** Localized entity name for "booking" (e.g. "booking", "appointment") */
  bookingEntityName: string;
}

/**
 * Displays the "Emails Sent" / "Last Sent" summary for a booking, along with a "see more"
 * link that opens a dialog listing up to the 10 most recent email notifications for the booking.
 * Shared between the desktop and mobile ViewBookingDialog components.
 */
const EmailEventLogsField = ({ open, bookingId, appDataSourceId, bookingEntityName }: EmailEventLogsFieldProps) => {
  const [isLoadingEmailEvent, setIsLoadingEmailEvent] = useState(false);
  const [hasLoadedEmailEvents, setHasLoadedEmailEvents] = useState(false);
  const [latestEmailEvents, setLatestEmailEvents] = useState<EmailEvent[]>([]);
  const [openRecentEmailLogsDialog, setOpenRecentEmailLogsDialog] = useState(false);

  const latestEmailEvent = latestEmailEvents[0] ?? null;
  const latestEmailEventTime = latestEmailEvent?.last_event_time ? dayjs(latestEmailEvent?.last_event_time) : null;

  const latestEmailSummary = useMemo(() => {
    if (!latestEmailEvent) return 'No data available';

    const eventStatus = latestEmailEvent?.status?.replaceAll('_', ' ') || 'Unknown status';
    return `${latestEmailEventTime?.format('MMM D, YYYY hh:mm A') || 'Time unavailable'} | To: ${latestEmailEvent?.to_email || 'Recipient unavailable'} | Status: ${eventStatus}`;
  }, [latestEmailEvent, latestEmailEventTime]);

  const getLatestEmailEventLog = useCallback(
    async ({ forceRefresh = false }: { forceRefresh?: boolean } = {}) => {
      try {
        setIsLoadingEmailEvent(true);
        const response = await callAzureFunction<EmailEvent | EmailEvent[]>({
          url: `/sendgrid/get-latest-email-event/${bookingId}`,
          method: 'get',
          params: {
            forceRefresh
          },
          appDataSourceId: appDataSourceId !== undefined ? String(appDataSourceId) : undefined
        });
        const emailEvents = Array.isArray(response.data) ? response.data : response.data ? [response.data] : [];
        setLatestEmailEvents(emailEvents);
        setHasLoadedEmailEvents(true);
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoadingEmailEvent(false);
      }
    },
    [bookingId, appDataSourceId]
  );

  const handleOpenRecentEmailLogsDialog = useCallback(() => {
    setOpenRecentEmailLogsDialog(true);
    if (!hasLoadedEmailEvents) {
      void getLatestEmailEventLog();
    }
  }, [getLatestEmailEventLog, hasLoadedEmailEvents]);

  useEffect(() => {
    if (!open) {
      setOpenRecentEmailLogsDialog(false);
    }
  }, [open]);

  useEffect(() => {
    if (bookingId) {
      setOpenRecentEmailLogsDialog(false);
      setHasLoadedEmailEvents(false);
      setLatestEmailEvents([]);
    }
  }, [bookingId]);

  return (
    <>
      {/* Email Notification */}
      <Grid size={{ xs: 12, sm: 6 }} alignItems="center" sx={{ display: 'flex' }}>
        {isLoadingEmailEvent ? (
          <Grid container direction="row" size={12}>
            <Grid container direction="row" alignItems="center">
              <Grid>
                <Typography display="inline" mr={1}>
                  Emails Sent:{' '}
                </Typography>
              </Grid>
              <Grid>
                <Skeleton width={205} />
              </Grid>
            </Grid>
            <Grid size={12}>
              <Skeleton width={335} />
            </Grid>
          </Grid>
        ) : (
          <Stack direction="column" gap={0.5} sx={{ width: '100%' }}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5}>
              <Typography>Emails Sent:</Typography>
              {!hasLoadedEmailEvents ? (
                <Button onClick={() => void getLatestEmailEventLog()} size="small" color="info">
                  <Typography style={{ fontSize: '0.8rem' }}>Find Emails</Typography>
                </Button>
              ) : (
                <>
                  <MUILink
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={handleOpenRecentEmailLogsDialog}
                    sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                  >
                    see more
                  </MUILink>
                  <Tooltip title="Force refresh email logs" placement="top">
                    <IconButton size="small" onClick={() => void getLatestEmailEventLog({ forceRefresh: true })} sx={{ p: 0.25 }}>
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Stack>
            {hasLoadedEmailEvents && <Typography>Last Sent: {latestEmailSummary}</Typography>}
          </Stack>
        )}
      </Grid>

      <Dialog open={open && openRecentEmailLogsDialog} onClose={() => setOpenRecentEmailLogsDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography variant="h4">Recent Email Logs</Typography>
            <IconButton
              size="small"
              onClick={() => getLatestEmailEventLog({ forceRefresh: true })}
              disabled={isLoadingEmailEvent}
              aria-label="refresh recent email logs"
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Up to 10 most recent email notifications for this {bookingEntityName}.
            <br />
            Note that sent emails may take several minutes to appear in this list.
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: 'grey.50' }}>
          {isLoadingEmailEvent ? (
            <Skeleton variant="rounded" height={88} />
          ) : latestEmailEvents.length ? (
            latestEmailEvents.map((emailEvent, index) => {
              const emailEventTime = emailEvent?.last_event_time ? dayjs(emailEvent.last_event_time) : null;
              const formattedStatus = emailEvent?.status?.replaceAll('_', ' ') || 'Unknown status';
              return (
                <Box
                  key={`${emailEvent?.msg_id || 'email-event'}-${index}`}
                  sx={{
                    mb: 1.25,
                    p: 1.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    backgroundColor: 'background.paper'
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Chip label={formattedStatus} size="small" sx={{ textTransform: 'capitalize' }} />
                    <Typography variant="caption" color="text.secondary">{`#${index + 1}`}</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 0.75 }}>
                    {emailEventTime ? emailEventTime.format('MMM D, YYYY hh:mm A') : 'Time unavailable'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {emailEvent?.to_email || 'Recipient unavailable'}
                  </Typography>
                  {emailEvent?.subject && (
                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                      {`Subject: ${emailEvent.subject}`}
                    </Typography>
                  )}
                </Box>
              );
            })
          ) : (
            <Typography color="text.secondary">No data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setOpenRecentEmailLogsDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailEventLogsField;
